import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Shield, User, Lock, Loader2 } from "lucide-react";
import { toast } from "sonner";
import PasswordInput from "@/components/PasswordInput";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function PoliceLogin() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API}/police/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password })
      });

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          throw new Error("Invalid username or password");
        }
        throw new Error("Login failed. Please try again.");
      }

      const text = await response.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch {
        throw new Error("Invalid response from server");
      }

      localStorage.setItem("police_session", data.session_token);
      localStorage.setItem("police_authority", data.authority_name);
      toast.success(`Welcome, ${data.contact_name}`);
      navigate("/police/portal");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a1628] flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center gap-3 mb-8">
          <div className="p-3 bg-blue-600 rounded-lg">
            <Shield className="w-10 h-10 text-white" />
          </div>
          <div>
            <img src="/toolio-logo-new.png" alt="TOOLIO" className="h-8 sm:h-10" />
            <p className="text-blue-400 text-sm font-medium">Police Portal</p>
          </div>
        </div>

        <Card className="border-blue-900/50 bg-[#0f2744]">
          <CardHeader className="text-center">
            <CardTitle className="font-heading text-xl uppercase text-white">Law Enforcement Access</CardTitle>
            <CardDescription className="text-blue-300/70">
              Enter your credentials to access the stolen tool database
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="username" className="text-blue-200">Username</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-400" />
                  <Input
                    id="username"
                    type="text"
                    placeholder="Enter username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="pl-10 bg-[#0a1628] border-blue-900 text-white placeholder:text-blue-400/50 focus:border-blue-500"
                    required
                    data-testid="police-username-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-blue-200">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-400 z-10" />
                  <PasswordInput
                    id="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 pr-10 bg-[#0a1628] border-blue-900 text-white placeholder:text-blue-400/50 focus:border-blue-500"
                    required
                    data-testid="police-password-input"
                  />
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold uppercase tracking-wide" 
                disabled={loading}
                data-testid="police-login-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Shield className="w-4 h-4 mr-2" />}
                Access Portal
              </Button>
            </form>

            <div className="mt-6 pt-4 border-t border-blue-900/50">
              <p className="text-xs text-blue-400/70 text-center">
                This portal is restricted to authorized law enforcement personnel only.
                Unauthorized access is prohibited and may be prosecuted.
              </p>
            </div>
          </CardContent>
        </Card>

        <p className="text-center mt-6 text-sm text-blue-400/50">
          <Link to="/" className="hover:text-blue-400">← Return to TOOLIO</Link>
        </p>
      </div>
    </div>
  );
}
